import React, { useState } from 'react';
import {
    Container,
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CardMedia,
    Grid,
    TextField,
    Chip,
    Stack,
    useTheme,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Rating
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupIcon from '@mui/icons-material/Group';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ShareIcon from '@mui/icons-material/Share';

interface TourPackage {
    id: number;
    name: string;
    destination: string;
    image: string;
    duration: string;
    price: number;
    rating: number;
    reviews: number;
    activities: string[];
    description: string;
    highlights: string[];
    status: 'available' | 'sold-out';
}

const SportTourism: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDark = theme.palette.mode === 'dark';

    const [packages] = useState<TourPackage[]>([
        {
            id: 1,
            name: 'Bali Badminton Adventure',
            destination: 'Bali, Indonesia',
            image: 'https://images.unsplash.com/photo-1537225228614-56cc3556d7ed?w=500&h=300&fit=crop',
            duration: '5 Hari 4 Malam',
            price: 5999000,
            rating: 4.8,
            reviews: 127,
            activities: ['Badminton', 'Beach', 'Diving', 'Cultural Tour'],
            description: 'Pengalaman badminton unik di Bali dengan pemandangan pantai yang menakjubkan',
            highlights: [
                'Main badminton di resort bintang lima',
                'Snorkeling di Nusa Dua',
                'Kunjungan ke Pura Besakih',
                'Dinner dengan pemandangan laut'
            ],
            status: 'available'
        },
        {
            id: 2,
            name: 'Bandung Mountain Sports Challenge',
            destination: 'Bandung, Indonesia',
            image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=500&h=300&fit=crop',
            duration: '3 Hari 2 Malam',
            price: 2999000,
            rating: 4.6,
            reviews: 89,
            activities: ['Badminton', 'Hiking', 'Hot Spring', 'Local Food'],
            description: 'Kombinasi sempurna antara olahraga badminton dan petualangan alam di Bandung',
            highlights: [
                'Kompetisi badminton antar komunitas',
                'Hiking ke Kawah Putih',
                'Berendam di hot spring',
                'Food tour Bandung'
            ],
            status: 'available'
        },
        {
            id: 3,
            name: 'Lombok Beach Sports Retreat',
            destination: 'Lombok, Indonesia',
            image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=500&h=300&fit=crop',
            duration: '4 Hari 3 Malam',
            price: 4799000,
            rating: 4.9,
            reviews: 156,
            activities: ['Badminton', 'Surfing', 'Beach Volleyball', 'Island Hopping'],
            description: 'Pelayaran ke surga tersembunyi Lombok dengan aktivitas olahraga pantai yang seru',
            highlights: [
                'Main badminton dengan pasir putih',
                'Surfing lessons dari instruktur profesional',
                'Island hopping ke Gili Islands',
                'Sunset party di pantai'
            ],
            status: 'available'
        },
        {
            id: 4,
            name: 'Yogyakarta Cultural Sports Tour',
            destination: 'Yogyakarta, Indonesia',
            image: 'https://images.unsplash.com/photo-1514632776622-658c47d1b308?w=500&h=300&fit=crop',
            duration: '3 Hari 2 Malam',
            price: 2499000,
            rating: 4.7,
            reviews: 94,
            activities: ['Badminton', 'Cultural Tour', 'Photography', 'Local Sports'],
            description: 'Jelajahi keindahan budaya Yogyakarta sambil mengasah keterampilan badminton',
            highlights: [
                'Tournament badminton di GOR modern',
                'Naik gunung Merapi',
                'Kunjungan ke Candi Borobudur',
                'Workshop dengan atlet profesional'
            ],
            status: 'sold-out'
        }
    ]);

    const [bookingOpen, setBookingOpen] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState<TourPackage | null>(null);
    const [successMessage, setSuccessMessage] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        participants: '',
        date: ''
    });

    const handleBooking = (pkg: TourPackage) => {
        if (pkg.status !== 'sold-out') {
            setSelectedPackage(pkg);
            setBookingOpen(true);
        }
    };

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmitBooking = () => {
        if (formData.name && formData.email && formData.phone && formData.participants) {
            setBookingOpen(false);
            setSuccessMessage(true);
            setFormData({ name: '', email: '', phone: '', participants: '', date: '' });
            setTimeout(() => setSuccessMessage(false), 5000);
        }
    };

    return (
        <>
            <Helmet>
                <title>Sport Tourism - Booking Mabar RaketerS</title>
                <meta name="description" content="Paket wisata olahraga badminton ke destinasi terbaik di Indonesia" />
            </Helmet>

            {/* Success Alert */}
            {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    ✓ Booking Anda berhasil! Kami akan menghubungi Anda dalam 24 jam.
                </Alert>
            )}

            {/* Hero Section */}
            <Box
                sx={{
                    background: isDark
                        ? 'linear-gradient(135deg, #1a3a52 0%, #0d47a1 50%, #1565c0 100%)'
                        : 'linear-gradient(135deg, #0288d1 0%, #01579b 50%, #006064 100%)',
                    backgroundAttachment: 'fixed',
                    color: '#fff',
                    py: isMobile ? 6 : 10,
                    textAlign: 'center',
                    borderRadius: 3,
                    mb: 6,
                    boxShadow: '0 30px 60px rgba(0,0,0,0.3)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: `
                            radial-gradient(circle at 20% 80%, rgba(255,255,255,0.15) 0%, transparent 50%),
                            radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)
                        `,
                        pointerEvents: 'none',
                    }
                }}
            >
                <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Box
                            sx={{
                                background: 'rgba(255,255,255,0.15)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '50%',
                                p: 3,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid rgba(255,255,255,0.2)',
                            }}
                        >
                            <FlightTakeoffIcon sx={{ fontSize: 60 }} />
                        </Box>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, textShadow: '0 4px 16px rgba(0,0,0,0.4)' }}>
                        Sport Tourism
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 3, opacity: 0.95, maxWidth: 650, mx: 'auto', lineHeight: 1.8 }}>
                        Jelajahi destinasi indah Indonesia sambil mengasah kemampuan badminton dengan komunitas RaketerS. Paket wisata olahraga yang menggabungkan petualangan, budaya, dan persahabatan.
                    </Typography>
                    <Stack direction={isMobile ? 'column' : 'row'} spacing={2} justifyContent="center">
                        <Button
                            variant="contained"
                            size={isMobile ? 'medium' : 'large'}
                            sx={{
                                background: '#fff',
                                color: '#0288d1',
                                fontWeight: 700,
                                px: 4,
                                borderRadius: 2,
                                '&:hover': {
                                    background: '#f5f5f5',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 12px 24px rgba(0,0,0,0.2)'
                                }
                            }}
                        >
                            Lihat Paket Wisata
                        </Button>
                        <Button
                            variant="outlined"
                            size={isMobile ? 'medium' : 'large'}
                            sx={{
                                borderColor: '#fff',
                                color: '#fff',
                                fontWeight: 700,
                                px: 4,
                                borderRadius: 2,
                                '&:hover': {
                                    background: 'rgba(255,255,255,0.1)',
                                    transform: 'translateY(-2px)',
                                }
                            }}
                        >
                            Info Lebih Lanjut
                        </Button>
                    </Stack>
                </Container>
            </Box>

            {/* Why Choose Us */}
            <Box sx={{ mb: 8 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
                    Kenapa Memilih Sport Tourism RaketerS?
                </Typography>
                <Grid container spacing={3}>
                    {[
                        {
                            icon: <EmojiEventsIcon sx={{ fontSize: 40 }} />,
                            title: 'Pengalaman Tak Terlupakan',
                            description: 'Kombinasi sempurna antara olahraga, petualangan, dan kebersamaan dengan teman-teman komunitas'
                        },
                        {
                            icon: <LocalActivityIcon sx={{ fontSize: 40 }} />,
                            title: 'Destinasi Pilihan',
                            description: 'Paket wisata ke destinasi terbaik di Indonesia dengan fasilitas kelas atas'
                        },
                        {
                            icon: <DirectionsBusIcon sx={{ fontSize: 40 }} />,
                            title: 'All-In Package',
                            description: 'Transportasi, akomodasi, dan aktivitas olahraga sudah termasuk dalam satu paket'
                        },
                        {
                            icon: <GroupIcon sx={{ fontSize: 40 }} />,
                            title: 'Komunitas Supportif',
                            description: 'Berbagi pengalaman dengan pemain badminton dari berbagai level dan latar belakang'
                        }
                    ].map((feature, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Card
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    p: 3,
                                    textAlign: 'center',
                                    borderRadius: 3,
                                    background: isDark
                                        ? 'rgba(255,255,255,0.05)'
                                        : 'rgba(2, 136, 209, 0.05)',
                                    border: isDark
                                        ? '1px solid rgba(255,255,255,0.1)'
                                        : '1px solid rgba(2, 136, 209, 0.2)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-10px)',
                                        boxShadow: isDark
                                            ? '0 12px 24px rgba(255,255,255,0.1)'
                                            : '0 12px 24px rgba(2, 136, 209, 0.2)',
                                        background: isDark
                                            ? 'rgba(255,255,255,0.1)'
                                            : 'rgba(2, 136, 209, 0.1)',
                                    }
                                }}
                                elevation={0}
                            >
                                <Box sx={{ color: '#0288d1', mb: 2 }}>
                                    {feature.icon}
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                    {feature.title}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    {feature.description}
                                </Typography>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Packages Section */}
            <Box sx={{ mb: 8 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
                    Paket Wisata Tersedia
                </Typography>
                <Grid container spacing={3}>
                    {packages.map((pkg) => (
                        <Grid item xs={12} md={6} lg={6} key={pkg.id}>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    transition: 'all 0.3s ease',
                                    border: isDark
                                        ? '1px solid rgba(255,255,255,0.1)'
                                        : '1px solid rgba(0,0,0,0.1)',
                                    display: 'flex',
                                    flexDirection: isMobile ? 'column' : 'row',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: isDark
                                            ? '0 20px 40px rgba(255,255,255,0.1)'
                                            : '0 20px 40px rgba(0,0,0,0.15)',
                                    }
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    image={pkg.image}
                                    alt={pkg.name}
                                    sx={{
                                        width: isMobile ? '100%' : '40%',
                                        height: isMobile ? 200 : '100%',
                                        minHeight: isMobile ? 200 : 300,
                                        objectFit: 'cover'
                                    }}
                                />
                                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                            {pkg.name}
                                        </Typography>
                                        {pkg.status === 'sold-out' && (
                                            <Chip
                                                label="Terjual Habis"
                                                color="error"
                                                size="small"
                                            />
                                        )}
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                                        <Rating value={pkg.rating} readOnly size="small" />
                                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                            ({pkg.reviews} ulasan)
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, opacity: 0.8 }}>
                                        <LocationOnIcon sx={{ fontSize: 18, color: '#0288d1' }} />
                                        <Typography variant="body2">
                                            {pkg.destination}
                                        </Typography>
                                    </Box>

                                    <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
                                        {pkg.description}
                                    </Typography>

                                    <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, opacity: 0.7 }}>
                                        Aktivitas:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                        {pkg.activities.map((activity, idx) => (
                                            <Chip
                                                key={idx}
                                                label={activity}
                                                size="small"
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>

                                    <Box sx={{ mt: 'auto' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Box>
                                                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                                    {pkg.duration}
                                                </Typography>
                                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0288d1' }}>
                                                    Rp {pkg.price.toLocaleString('id-ID')}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button size="small" variant="text" startIcon={<BookmarkIcon />} />
                                                <Button size="small" variant="text" startIcon={<ShareIcon />} />
                                            </Box>
                                        </Box>
                                        <Button
                                            fullWidth
                                            variant={pkg.status === 'sold-out' ? 'outlined' : 'contained'}
                                            onClick={() => handleBooking(pkg)}
                                            disabled={pkg.status === 'sold-out'}
                                            sx={{
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                py: 1.2
                                            }}
                                        >
                                            {pkg.status === 'sold-out' ? 'Terjual Habis' : 'Booking Sekarang'}
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* CTA Section */}
            <Box
                sx={{
                    background: isDark
                        ? 'linear-gradient(135deg, #1a3a52 0%, #0d47a1 100%)'
                        : 'linear-gradient(135deg, #0288d1 0%, #01579b 100%)',
                    color: '#fff',
                    py: 6,
                    px: 3,
                    borderRadius: 3,
                    textAlign: 'center',
                    mb: 4
                }}
            >
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                    Petualangan Menanti!
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, maxWidth: 600, mx: 'auto', opacity: 0.95 }}>
                    Dapatkan diskon spesial 15% untuk pemesanan paket grup. Hubungi kami untuk mengetahui lebih lanjut tentang penawaran menarik kami.
                </Typography>
                <Stack direction={isMobile ? 'column' : 'row'} spacing={2} justifyContent="center">
                    <Button
                        variant="contained"
                        size="large"
                        sx={{
                            background: '#fff',
                            color: '#0288d1',
                            fontWeight: 700,
                            px: 4,
                            borderRadius: 2,
                            '&:hover': {
                                background: '#f5f5f5',
                            }
                        }}
                    >
                        Chat WhatsApp
                    </Button>
                    <Button
                        variant="outlined"
                        size="large"
                        sx={{
                            borderColor: '#fff',
                            color: '#fff',
                            fontWeight: 700,
                            px: 4,
                            borderRadius: 2,
                            '&:hover': {
                                background: 'rgba(255,255,255,0.1)',
                            }
                        }}
                    >
                        Hubungi Kami
                    </Button>
                </Stack>
            </Box>

            {/* Booking Dialog */}
            <Dialog open={bookingOpen} onClose={() => setBookingOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1.3rem' }}>
                    Booking Paket Wisata
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {selectedPackage && (
                        <>
                            <Box sx={{ mb: 3, p: 2, background: 'rgba(2, 136, 209, 0.1)', borderRadius: 1 }}>
                                <Typography variant="body2">
                                    <strong>Paket:</strong> {selectedPackage.name}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Harga:</strong> Rp {selectedPackage.price.toLocaleString('id-ID')}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Durasi:</strong> {selectedPackage.duration}
                                </Typography>
                            </Box>
                            <TextField
                                fullWidth
                                label="Nama Lengkap"
                                name="name"
                                value={formData.name}
                                onChange={handleFormChange}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleFormChange}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Nomor Telepon"
                                name="phone"
                                value={formData.phone}
                                onChange={handleFormChange}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Jumlah Peserta"
                                name="participants"
                                type="number"
                                value={formData.participants}
                                onChange={handleFormChange}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Tanggal Keberangkatan"
                                name="date"
                                type="date"
                                value={formData.date}
                                onChange={handleFormChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={() => setBookingOpen(false)}>Batal</Button>
                    <Button
                        onClick={handleSubmitBooking}
                        variant="contained"
                        disabled={!formData.name || !formData.email || !formData.phone || !formData.participants}
                    >
                        Kirim Booking
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SportTourism;
